import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Loader2, Sparkles, Crown, Gem, ShieldCheck, Zap, Star, Rocket } from "lucide-react";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";
import { trackMetaEvent } from "@/lib/metaPixel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SHOW_TRIAL_PLAN } from "@/lib/featureFlags";

// Stable, public Stripe price IDs (test mode) — ticket packs only.
const PRICE_TRIAL = "price_1TWG37PZdT2kgzcUySUQl1tK";
const PRICE_PACK_10 = "price_1TWFfXPZdT2kgzcU1rseJRDv";
const PRICE_PACK_30 = "price_1TWFgEPZdT2kgzcUQquvSvP2";
const PRICE_PACK_100 = "price_1TWFswPZdT2kgzcUYBOX4FQu";

type Mode = "payment" | "subscription";
type BillingCycle = "annual" | "monthly";
type PlanKey = "light" | "standard" | "pro";

type ShopItem = {
  key: string;
  priceId: string;
  mode: Mode;
  title: string;
  price: string;
  perTicket?: string;
  caption: string;
  badge?: string;
  highlight?: boolean;
  subscriberOnly: boolean;
  icon: typeof Sparkles;
  plan?: PlanKey;
  cycle?: BillingCycle;
  monthlyAmount?: number;
  savingsPerMonth?: number;
};

// Fallback to monthly IDs when annual env vars are not yet configured.
const env = import.meta.env as Record<string, string | undefined>;
const PRICE_LIGHT_MONTHLY = env.VITE_STRIPE_PRICE_LIGHT_MONTHLY ?? "price_1TWF6QPZdT2kgzcUTs0ZKeLK";
const PRICE_STANDARD_MONTHLY = env.VITE_STRIPE_PRICE_STANDARD_MONTHLY ?? "price_1TWFccPZdT2kgzcUZXIQ2DDk";
const PRICE_PRO_MONTHLY = env.VITE_STRIPE_PRICE_PRO_MONTHLY ?? "price_1TWFeSPZdT2kgzcUXQLBKCw0";
const PRICE_LIGHT_ANNUAL = env.VITE_STRIPE_PRICE_LIGHT_ANNUAL ?? PRICE_LIGHT_MONTHLY;
const PRICE_STANDARD_ANNUAL = env.VITE_STRIPE_PRICE_STANDARD_ANNUAL ?? PRICE_STANDARD_MONTHLY;
const PRICE_PRO_ANNUAL = env.VITE_STRIPE_PRICE_PRO_ANNUAL ?? PRICE_PRO_MONTHLY;

const ALL_TICKET_ITEMS: ShopItem[] = [
  {
    key: "trial",
    priceId: PRICE_TRIAL,
    mode: "payment",
    title: "お試しクレジット 80",
    price: "¥500",
    perTicket: "1クレジットあたり ¥6.25",
    caption: "単発購入",
    subscriberOnly: false,
    icon: Sparkles,
  },
  {
    key: "pack10",
    priceId: PRICE_PACK_10,
    mode: "payment",
    title: "200クレジットパック",
    price: "¥1,960",
    perTicket: "1クレジットあたり ¥9.80",
    caption: "ちょっと足したいときに",
    subscriberOnly: true,
    icon: Gem,
  },
  {
    key: "pack30",
    priceId: PRICE_PACK_30,
    mode: "payment",
    title: "540クレジットパック",
    price: "¥4,760",
    perTicket: "1クレジットあたり ¥8.81",
    caption: "標準サイズのパック（200パックより約1割お得）",
    subscriberOnly: true,
    highlight: true,
    icon: Crown,
  },
  {
    key: "pack100",
    priceId: PRICE_PACK_100,
    mode: "payment",
    title: "1,570クレジットパック",
    price: "¥13,150",
    perTicket: "1クレジットあたり ¥8.38",
    caption: "ヘビーユーザー向け（最もお得）",
    subscriberOnly: true,
    icon: Crown,
  },
];

const TICKET_ITEMS = ALL_TICKET_ITEMS.filter(
  (i) => SHOW_TRIAL_PLAN || i.key !== "trial",
);

type PlanDef = {
  plan: PlanKey;
  title: string;
  caption: string;
  perTicket: string;
  icon: typeof Sparkles;
  highlight?: boolean;
  annual: { priceId: string; monthlyAmount: number };
  monthly: { priceId: string; monthlyAmount: number };
};

const PLAN_DEFS: PlanDef[] = [
  {
    plan: "light",
    title: "Light",
    caption: "気軽に試したい方に",
    perTicket: "毎月300クレジット",
    icon: Zap,
    annual: { priceId: PRICE_LIGHT_ANNUAL, monthlyAmount: 2980 },
    monthly: { priceId: PRICE_LIGHT_MONTHLY, monthlyAmount: 3980 },
  },
  {
    plan: "standard",
    title: "Standard",
    caption: "定期的に作る方に",
    perTicket: "毎月1,000クレジット",
    icon: Star,
    highlight: true,
    annual: { priceId: PRICE_STANDARD_ANNUAL, monthlyAmount: 8800 },
    monthly: { priceId: PRICE_STANDARD_MONTHLY, monthlyAmount: 11800 },
  },
  {
    plan: "pro",
    title: "Pro",
    caption: "ヘビーユーザー向け",
    perTicket: "毎月3,000クレジット",
    icon: Rocket,
    annual: { priceId: PRICE_PRO_ANNUAL, monthlyAmount: 24800 },
    monthly: { priceId: PRICE_PRO_MONTHLY, monthlyAmount: 32800 },
  },
];

const yen = (n: number) => `¥${n.toLocaleString("ja-JP")}`;

function buildPlanItems(cycle: BillingCycle): ShopItem[] {
  return PLAN_DEFS.map((p) => {
    const cur = cycle === "annual" ? p.annual : p.monthly;
    const savings = cycle === "annual" ? p.monthly.monthlyAmount - p.annual.monthlyAmount : 0;
    return {
      key: `plan_${p.plan}_${cycle}`,
      priceId: cur.priceId,
      mode: "subscription" as const,
      title: p.title,
      price: `${yen(cur.monthlyAmount)} / 月`,
      perTicket: p.perTicket,
      caption: p.caption,
      highlight: p.highlight,
      subscriberOnly: false,
      icon: p.icon,
      plan: p.plan,
      cycle,
      monthlyAmount: cur.monthlyAmount,
      savingsPerMonth: savings,
    };
  });
}

// TODO(stripe-dashboard): Stripe側の商品名・説明文を以下に手動更新する：
//   - Trial Ticket → 「お試しクレジット 50」（¥500）
//   - Pack 10  → 「300クレジットパック」
//   - Pack 30  → 「820クレジットパック」
//   - Pack 100 → 「2,390クレジットパック」
//   - Light/Standard/Pro の説明文に "毎月N クレジット" を反映

export const Route = createFileRoute("/_authenticated/credits-shop")({
  head: () => ({
    meta: [
      { title: "クレジット購入 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CreditsShopPage,
});

function CreditsShopPage() {
  const [user, setUser] = useState<User | null>(null);
  const [balanceSub, setBalanceSub] = useState<number>(0);
  const [balancePurchased, setBalancePurchased] = useState<number>(0);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [activePlan, setActivePlan] = useState<string | null>(null);
  const [activeContract, setActiveContract] = useState<string | null>(null);
  const [minimumTermEnd, setMinimumTermEnd] = useState<string | null>(null);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [cycle, setCycle] = useState<BillingCycle>("annual");
  const [consentItem, setConsentItem] = useState<ShopItem | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);

  useEffect(() => {
    let active = true;
    supabase.auth.getUser().then(({ data }) => {
      if (active) setUser(data.user ?? null);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      const [{ data: credits }, { data: subs }] = await Promise.all([
        supabase
          .from("credits")
          .select("credit_subscription_balance, credit_purchased_balance, expires_at")
          .eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("subscriptions")
          .select("plan, status, contract_type, minimum_term_end")
          .eq("user_id", user.id)
          .in("status", ["active", "trialing"])
          .limit(1),
      ]);
      if (!active) return;
      setBalanceSub(credits?.credit_subscription_balance ?? 0);
      setBalancePurchased(credits?.credit_purchased_balance ?? 0);
      setExpiresAt(credits?.expires_at ?? null);
      const sub = subs?.[0];
      setActivePlan(sub?.plan ?? null);
      setActiveContract(sub?.contract_type ?? null);
      setMinimumTermEnd(sub?.minimum_term_end ?? null);
    })();
    return () => {
      active = false;
    };
  }, [user]);

  const hasActiveSub = activePlan !== null;
  const total = balanceSub + balancePurchased;
  const expiresLabel = useMemo(() => {
    if (!expiresAt) return null;
    try {
      const d = new Date(expiresAt);
      return d.toLocaleDateString("ja-JP", { year: "numeric", month: "long", day: "numeric" });
    } catch {
      return null;
    }
  }, [expiresAt]);

  const minTermLabel = useMemo(() => {
    if (!minimumTermEnd) return null;
    try {
      return new Date(minimumTermEnd).toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return null;
    }
  }, [minimumTermEnd]);

  const inAnnualTerm = useMemo(() => {
    if (activeContract !== "annual" || !minimumTermEnd) return false;
    try {
      return new Date(minimumTermEnd).getTime() > Date.now();
    } catch {
      return false;
    }
  }, [activeContract, minimumTermEnd]);

  const planItems = useMemo(() => buildPlanItems(cycle), [cycle]);

  const startCheckout = async (item: ShopItem) => {
    if (!user) {
      toast.error("ログインが必要です");
      return;
    }
    // Stripe Checkout cannot render inside an iframe (editor preview), so only
    // pre-open a tab there; on the real site, redirect in the same tab.
    const inIframe = (() => {
      try {
        return window.self !== window.top;
      } catch {
        return true;
      }
    })();
    const checkoutWindow = inIframe ? window.open("about:blank", "_blank") : null;
    setLoadingKey(item.key);
    try {
      const valueJpy = Number(item.price.replace(/[^\d]/g, "")) || undefined;
      trackMetaEvent("InitiateCheckout", {
        content_ids: [item.priceId],
        content_name: item.title,
        content_category: item.mode,
        currency: "JPY",
        value: valueJpy,
      });
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: { price_id: item.priceId, mode: item.mode },
      });
      let errCode: string | null = null;
      if (error && (error as { context?: Response }).context) {
        try {
          const ctx = (error as { context: Response }).context;
          const body = await ctx.clone().json();
          errCode = body?.error ?? null;
        } catch {
          /* ignore */
        }
      }
      if (errCode === "TRIAL_LIMIT_REACHED") {
        checkoutWindow?.close();
        toast.error("お試しクレジットの購入上限に達しました", {
          description: "お試し購入は1ユーザーあたり2回までです。",
        });
        setLoadingKey(null);
        return;
      }
      if (errCode === "SUBSCRIBER_ONLY") {
        checkoutWindow?.close();
        toast.error("追加クレジットパックはサブスク会員限定です");
        setLoadingKey(null);
        return;
      }
      if (error) throw error;
      const url = (data as { url?: string })?.url;
      if (!url) throw new Error("Checkout URL not returned");
      if (checkoutWindow) {
        checkoutWindow.location.href = url;
      } else {
        window.location.assign(url);
        return;
      }
      setLoadingKey(null);
    } catch (e) {
      checkoutWindow?.close();
      console.error(e);
      toast.error("決済画面の準備に失敗しました", {
        description: e instanceof Error ? e.message : undefined,
      });
      setLoadingKey(null);
    }
  };

  const handleCheckout = async (item: ShopItem) => {
    if (item.subscriberOnly && !hasActiveSub) {
      toast.error("追加クレジットパックはサブスク会員限定です", {
        description: "まずはサブスクリプションプランをご検討ください。",
      });
      return;
    }
    // Guard: annual→monthly during minimum term is not allowed.
    if (
      item.mode === "subscription" &&
      item.cycle === "monthly" &&
      inAnnualTerm
    ) {
      toast.error("年契約期間中は月契約への変更ができません", {
        description: minTermLabel
          ? `年契約期間（〜${minTermLabel}）終了後にご変更ください。`
          : "契約期間終了後にご変更ください。",
      });
      return;
    }
    // Annual subscription requires explicit consent.
    if (item.mode === "subscription" && item.cycle === "annual") {
      setConsentChecked(false);
      setConsentItem(item);
      return;
    }
    await startCheckout(item);
  };

  const renderCard = (item: ShopItem) => {
    const Icon = item.icon;
    const disabled = item.subscriberOnly && !hasActiveSub;
    const isCurrentPlan = item.plan != null && activePlan === item.plan;
    const loading = loadingKey === item.key;
    const isAnnual = item.cycle === "annual";
    const isMonthlyPlan = item.cycle === "monthly";
    return (
      <Card
        key={item.key}
        className={
          item.highlight
            ? "relative border-primary/60 shadow-md ring-1 ring-primary/30"
            : "relative"
        }
      >
        {item.badge && !isCurrentPlan && (
          <Badge className="absolute -top-2 right-4">{item.badge}</Badge>
        )}
        {isCurrentPlan && (
          <Badge variant="secondary" className="absolute -top-2 right-4">
            現在のプラン
          </Badge>
        )}
        <CardHeader>
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">{item.title}</CardTitle>
          </div>
          <CardDescription>{item.caption}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-3xl font-bold">{item.price}</p>
            {item.perTicket && (
              <p className="text-xs text-muted-foreground">{item.perTicket}</p>
            )}
            {isAnnual && (
              <p className="mt-1 text-[11px] text-muted-foreground">1年契約・月払い（12ヶ月契約）</p>
            )}
            {isMonthlyPlan && (
              <p className="mt-1 text-[11px] text-muted-foreground">月契約・いつでも解約OK</p>
            )}
            {isAnnual && item.savingsPerMonth != null && item.savingsPerMonth > 0 && (
              <p className="mt-2 inline-block rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                月払い比 {yen(item.savingsPerMonth)}/月 お得
              </p>
            )}
          </div>
          {isCurrentPlan ? (
            <Button className="w-full" variant="outline" disabled>
              ご利用中
            </Button>
          ) : disabled ? (
            <Button className="w-full" variant="outline" disabled>
              サブスク会員限定
            </Button>
          ) : (
            <Button
              className="w-full"
              onClick={() => handleCheckout(item)}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  準備中...
                </>
              ) : item.mode === "subscription" ? (
                hasActiveSub ? "プランを変更" : "このプランを始める"
              ) : (
                "購入する"
              )}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  const consentTotal =
    consentItem?.monthlyAmount != null ? consentItem.monthlyAmount * 12 : 0;

  return (
    <main className="min-h-screen bg-background px-4 py-8 md:px-6 md:py-10">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <header className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">プラン & クレジット</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            毎月クレジットが付与されます。年契約なら月払い比 <strong>最大 ¥8,000/月</strong> お得です。
          </p>
        </header>

        {/* Balance */}
        <Card className="mb-8 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="flex flex-col gap-3 p-5 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <Gem className="h-6 w-6 text-primary" />
              <div>
                <p className="text-2xl font-bold">💎 {total} クレジット</p>
                <p className="text-xs text-muted-foreground">
                  サブスク：{balanceSub} / 購入：{balancePurchased}
                </p>
              </div>
            </div>
            {expiresLabel && balancePurchased > 0 && (
              <p className="text-xs text-muted-foreground">
                購入分は <span className="font-semibold">{expiresLabel}</span> まで有効
              </p>
            )}
          </CardContent>
        </Card>

        {/* Subscription plans */}
        <section className="mb-10">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-xl font-bold tracking-tight">サブスクリプション</h2>
              <p className="text-sm text-muted-foreground">
                年契約なら月払い比 最大 ¥8,000/月 お得。
              </p>
            </div>
            <Tabs value={cycle} onValueChange={(v) => setCycle(v as BillingCycle)}>
              <TabsList>
                <TabsTrigger value="annual" className="gap-2">
                  年契約
                  <Badge className="h-5 px-1.5 text-[10px]">3ヶ月分お得</Badge>
                </TabsTrigger>
                <TabsTrigger value="monthly">月契約</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {planItems.map(renderCard)}
          </div>
          {activeContract === "annual" && minTermLabel && (
            <p className="mt-3 text-xs text-muted-foreground">
              現在の年契約は <strong>{minTermLabel}</strong> まで継続します。期間中は月契約への変更はできません。
            </p>
          )}
        </section>

        {/* Ticket packs */}
        <section className="mb-10">
          <div className="mb-4">
            <h2 className="text-xl font-bold tracking-tight">クレジット購入</h2>
            <p className="text-sm text-muted-foreground">
              サブスク会員は追加で買い増しできます（購入分は180日間有効）。
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {TICKET_ITEMS.map(renderCard)}
          </div>
        </section>

        {/* Notes */}
        <section className="mt-8 rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground">
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" />
            ご利用にあたって
          </h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>月契約はいつでも解約可能で、当月分は最後までご利用いただけます。</li>
            <li>年契約（12ヶ月継続の月払い）は途中解約でも12ヶ月目まで請求が続きます。</li>
            <li>購入したクレジットは購入から <strong>180日間</strong> 有効です。</li>
            <li>サブスク分のクレジットを優先的に消費します。</li>
            <li>決済は <strong>Stripe</strong> で安全に処理されます。</li>
          </ul>
        </section>

        <div className="mt-6 text-center">
          <Link
            to="/billing"
            className="text-sm text-primary underline-offset-4 hover:underline"
          >
            過去の購入履歴を見る →
          </Link>
        </div>
      </div>

      {/* Annual consent modal */}
      <Dialog
        open={consentItem !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConsentItem(null);
            setConsentChecked(false);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>1年契約（月払い）の同意</DialogTitle>
            <DialogDescription>
              以下の内容をご確認の上、お申し込みください。
            </DialogDescription>
          </DialogHeader>
          {consentItem && (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <p className="font-semibold">{consentItem.title} プラン</p>
                <p className="mt-1 text-muted-foreground">
                  月 {yen(consentItem.monthlyAmount ?? 0)} × 12ヶ月 ={" "}
                  <span className="font-semibold text-foreground">{yen(consentTotal)}</span>
                </p>
              </div>
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                <li>途中解約しても <strong>12ヶ月目までの請求は継続</strong> します。</li>
                <li>12ヶ月経過後は自動更新されます（1ヶ月前の通知でスキップ可能）。</li>
                <li>違約金は一切かかりません。</li>
              </ul>
              <label className="flex items-start gap-2 rounded-md border border-border p-3">
                <Checkbox
                  id="annual-consent"
                  checked={consentChecked}
                  onCheckedChange={(v) => setConsentChecked(v === true)}
                />
                <span className="text-sm leading-snug">
                  上記内容に同意して1年契約で申し込みます
                </span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConsentItem(null);
                setConsentChecked(false);
              }}
            >
              キャンセル
            </Button>
            <Button
              disabled={!consentChecked || loadingKey !== null}
              onClick={async () => {
                if (!consentItem) return;
                const item = consentItem;
                setConsentItem(null);
                await startCheckout(item);
              }}
            >
              同意して次へ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
